import React, { useCallback, useState } from "react";
import classNames from "classnames";
import { ButtonBase, IconButton, Popover, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { ReactComponent as LanguageGlyphIcon } from "../../assets/icons/language.svg";
import i18n, {
  EW_I18N_DISPLAY_LOCALES,
  resolveEwDisplayLocaleCode,
} from "../../i18n";
import { setAppLanguage } from "./LanguageControl";

export type LanguageMenuVariant = "icon" | "labeled";

export interface LanguageMenuProps {
  /** `icon` — header style; `labeled` — globe + translated “Language” for onboarding, etc. */
  variant?: LanguageMenuVariant;
  /** When true, the menu opens above the trigger (e.g. footer rows near the viewport bottom). */
  menuAbove?: boolean;
}

/**
 * Header & mobile language control: icon + floating Popover menu.
 * Uses Material-UI Popover to portal above modals, drawers, and scroll containers.
 */
export default function LanguageMenu({
  variant = "icon",
  menuAbove = false,
}: LanguageMenuProps) {
  const { t } = useTranslation("header");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const currentInMenu =
    resolveEwDisplayLocaleCode(i18n.resolvedLanguage ?? i18n.language) || null;
  const triggerAria = t("language.aria.menu");

  const anchorOrigin = menuAbove
    ? ({ vertical: "top", horizontal: "center" } as const)
    : ({ vertical: "bottom", horizontal: "right" } as const);

  const transformOrigin = menuAbove
    ? ({ vertical: "bottom", horizontal: "center" } as const)
    : ({ vertical: "top", horizontal: "right" } as const);

  return (
    <div
      className={classNames(
        "ew-app-dropdown",
        open && "ew-app-dropdown--open",
        menuAbove && "ew-app-dropdown--anchor-bottom",
      )}
    >
      {variant === "labeled" ? (
        <ButtonBase
          aria-label={triggerAria}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={handleClick}
          className="ew-language-menu-trigger ew-language-menu-trigger--labeled"
          disableRipple
        >
          <LanguageGlyphIcon
            className="ew-language-menu-trigger__glyph"
            style={{ width: 20, height: 20, display: "block" }}
            aria-hidden
          />
          <Typography
            component="span"
            variant="body2"
            className="ew-language-menu-trigger__label"
          >
            {triggerAria}
          </Typography>
        </ButtonBase>
      ) : (
        <IconButton
          aria-label={triggerAria}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={handleClick}
          component="span"
        >
          <LanguageGlyphIcon
            className="ew-language-menu-trigger__glyph"
            style={{ width: 20, height: 20, display: "block" }}
            aria-hidden
          />
        </IconButton>
      )}
      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        disableScrollLock
        style={{ zIndex: 10060 }}
        PaperProps={{
          className: "ew-header-menu-paper ew-language-menu-popover-paper",
          elevation: 0,
        }}
      >
        <ul
          className="ew-dropdown-panel ew-dropdown-panel--header-nav"
          id="ew-language-menu"
          role="listbox"
          style={{
            margin: 0,
            padding: "8px 6px",
            listStyle: "none",
            maxHeight: "320px",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <li className="ew-dropdown-panel__title-row" role="presentation">
            {t("language.labels.menuTitle")}
          </li>
          {EW_I18N_DISPLAY_LOCALES.map((loc) => (
            <li key={loc.code} role="presentation" style={{ listStyle: "none" }}>
              <button
                type="button"
                role="option"
                aria-selected={currentInMenu === loc.code}
                className={classNames(
                  "ew-dropdown-option",
                  currentInMenu === loc.code && "ew-dropdown-option--active",
                )}
                onClick={() => {
                  setAppLanguage(loc.code);
                  handleClose();
                }}
              >
                {loc.label}
              </button>
            </li>
          ))}
        </ul>
      </Popover>
    </div>
  );
}
